import React, { useState, useEffect } from 'react';
import { Link, useHistory } from 'react-router-dom';

import { useQuery, useMutation, useQueryClient } from '@sopranodesign/pulse-hooks';
import {
  PageWrapper,
  Placeholder,
  Flex,
  Divider,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableActions,
  OrderableTableRow,
  useOrderableTableReducer,
  StretchPanel as Panel,
  FormHeading,
  useNotification,
  ModalPortal,
  ModalContainer,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  Explainer
} from '@sopranodesign/pulse-components';
import { PermissionFilter } from '@sopranodesign/pulse-auth';

import { McpProviderDetails } from '../Components/index';
import { updateProvider, deleteSkill, discoverSkills } from '../api/index';

const EditMcpProvider = ({ match, location }) => {
  const { showSuccess, showError } = useNotification();
  const queryClient = useQueryClient();
  const history = useHistory();

  const id = match.params.id;

  const query = useQuery({ queryKey: [`mcp-skill-providers/${id}`] });

  const skillsQuery = useQuery({
    queryKey: ['mcp-skills'],
    select: data => data.data.filter(s => s.mcp_skill_provider_id === Number(id))
  });

  useEffect(() => {
    const oauthStatus = new URLSearchParams(location.search).get('oauth');

    if (!oauthStatus) { return; }

    if (oauthStatus === 'connected') {
      showSuccess('Connected via OAuth2');
      queryClient.invalidateQueries(`mcp-skill-providers/${id}`);
    } else if (oauthStatus === 'denied') {
      showError('OAuth2 connection was not completed');
    }

    history.replace({ search: '' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [deletingSkill, setDeletingSkill] = useState(null);

  const mutation = useMutation({
    mutationFn: updateProvider,
    onSuccess: () => {
      showSuccess('Provider updated');
      queryClient.invalidateQueries(`mcp-skill-providers/${id}`);
      queryClient.invalidateQueries('mcp-skill-providers');
    }
  });

  const deleteMutation = useMutation({
    mutationFn: deleteSkill,
    onSuccess: () => {
      showSuccess('Skill deleted');
      setDeletingSkill(null);
      queryClient.invalidateQueries('mcp-skills');
    }
  });

  const discoverMutation = useMutation({
    mutationFn: () => discoverSkills(id),
    onSuccess: data => {
      const { created = [], updated = [] } = data?.data ?? {};
      const total = created.length + updated.length;
      showSuccess(
        total > 0
          ? `Discovered ${created.length} new and ${updated.length} updated skill(s)`
          : 'No new skills found — already up to date'
      );
      queryClient.invalidateQueries('mcp-skills');
    }
  });

  const skillsOrder = useOrderableTableReducer({ id: 'mcp-provider-skills' });

  const sortedSkills = React.useMemo(() => {
    const data = skillsQuery.data ?? [];
    if (!skillsOrder.order || !skillsOrder.direction) { return data; }
    return [...data].sort((a, b) => {
      let aVal = a[skillsOrder.order];
      let bVal = b[skillsOrder.order];
      if (typeof aVal === 'boolean') {
        aVal = aVal ? 1 : 0;
        bVal = bVal ? 1 : 0;
      } else {
        aVal = String(aVal ?? '').toLowerCase();
        bVal = String(bVal ?? '').toLowerCase();
      }
      if (aVal < bVal) { return skillsOrder.direction === 'ascending' ? -1 : 1; }
      if (aVal > bVal) { return skillsOrder.direction === 'ascending' ? 1 : -1; }
      return 0;
    });
  }, [skillsQuery.data, skillsOrder.order, skillsOrder.direction]);

  const handleDeleteSkill = skill => () => setDeletingSkill(skill);

  return (
    <PageWrapper
      header="Edit MCP Provider"
      subheader="Update provider settings">
      {deletingSkill && (
        <ModalPortal>
          <ModalContainer>
            <ModalContent>
              <ModalHeader>Delete {deletingSkill.name}?</ModalHeader>
              <ModalBody>
                <Explainer type="warning">
                  This skill will be permanently removed. It can be re-discovered from the provider at any time.
                </Explainer>
              </ModalBody>
              <ModalFooter>
                <Flex right xSpace>
                  <Button variant="secondary" onClick={() => setDeletingSkill(null)}>
                    Cancel
                  </Button>
                  <Button
                    variant="secondary"
                    colour="danger"
                    icon="trash"
                    loading={deleteMutation.isPending}
                    onClick={() => deleteMutation.mutate(deletingSkill.id)}>
                    Delete
                  </Button>
                </Flex>
              </ModalFooter>
            </ModalContent>
          </ModalContainer>
        </ModalPortal>
      )}
      {query.isLoading && <Placeholder />}
      {query.isSuccess && (
        <McpProviderDetails
          handleSubmit={form => mutation.mutate({ id, ...form })}
          loading={mutation.isPending}
          initialProvider={query.data.data}
        />
      )}

      <Divider tall />

      <Panel>
        <Flex between center pad>
          <FormHeading>Skills</FormHeading>
          <Flex xSpace>
            <Button
              icon="search"
              loading={discoverMutation.isPending}
              onClick={() => discoverMutation.mutate()}
              aria-label="Discover skills">
              Discover Skills
            </Button>
          </Flex>
        </Flex>

        <Table loading={skillsQuery.isLoading} loadingColumns={3}>
          <TableHead>
            <OrderableTableRow
              rows={[
                { label: 'Name', sort: 'name' },
                { label: 'Description', sort: 'description' },
                { label: 'Status', sort: 'enabled' },
                null
              ]}
              {...skillsOrder.props}
            />
          </TableHead>
          <TableBody>
            {sortedSkills.map(skill => (
              <TableRow key={skill.id}>
                <TableCell>{skill.name}</TableCell>
                <TableCell>{skill.description}</TableCell>
                <TableCell>{skill.enabled ? 'Enabled' : 'Disabled'}</TableCell>
                <TableActions>
                  <Button
                    as={Link}
                    to={`/mcp/skills/${skill.id}`}
                    icon="pencilAlt"
                    aria-label="Edit skill"
                  > Edit </Button>
                  <PermissionFilter can="delete mcp skills">
                    <Button
                      icon="trash"
                      colour="danger"
                      aria-label="Delete skill"
                      onClick={handleDeleteSkill(skill)}
                    > Delete </Button>
                  </PermissionFilter>
                </TableActions>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Panel>
    </PageWrapper>
  );
};

export default EditMcpProvider;
